import { memo } from "react";
import type { PeerSnapshot, FallbackTier } from "@kmitl/shared";
import { PanelHeader } from "./PanelHeader";

interface Props {
  peers: PeerSnapshot[];
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
}

/**
 * Country code, not a flag.
 *
 * Regional-indicator flag emoji are not a reliable mark: Windows renders
 * "TH" / "SG" / "HK" as letter pairs rather than flags, macOS renders full
 * colour, and the fallback (🏳) is a white flag that reads as "no country" on
 * one platform and as a blank box on another. A table of university rankings
 * must say the same thing on every machine, so the ISO code is printed
 * directly in a small mono badge.
 */
function countryCode(country: string): string {
  const c = (country ?? "").trim().toUpperCase();
  return /^[A-Z]{2}$/.test(c) ? c : "··";
}

export const PeerComparison = memo(function PeerComparison({ peers, ageMinutes, fallbackTier }: Props) {
  if (peers.length === 0) return null;

  return (
    <div className="peer-comp">
      <PanelHeader
        title="PEER CONTEXT"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
        source="qs·the"
        actions={<span className="mono caption">{peers.length} UNIS</span>}
      />
      <div className="peer-table">
        <div className="peer-row peer-header-row mono">
          <span className="peer-cell">Uni</span>
          <span className="peer-cell">QS</span>
          <span className="peer-cell">THE</span>
          <span className="peer-cell">Students</span>
          <span className="peer-cell">Intl%</span>
        </div>
        {peers.map((p) => (
          <div key={p.name} className="peer-row">
            <span className="peer-cell peer-name">
              <span className="peer-flag mono" aria-hidden>{countryCode(p.country)}</span>
              {p.name}
            </span>
            <span className="peer-cell mono">{p.qsWorldRank}</span>
            <span className="peer-cell mono">{p.theWorldRank}</span>
            <span className="peer-cell mono">{p.studentsTotal.toLocaleString("en-US")}</span>
            <span className="peer-cell mono">{p.internationalPct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
});
